import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Popover, CloseButton, TextField, Grid, GridItem, MenuItem, Button, useStateInRef } from 'seagull';
import cloneDeep from 'lodash/cloneDeep';
import styles from "./ServicePriceEditor.module.scss";
import { DiscountType } from "../../models/service/model";
import Decimal from 'decimal.js';
import { utils } from "../../utils";
function resetForm(cost, setPrice, setDiscount, setDiscountType) {
  var _cost$discount$type, _cost$discount;
  setDiscount(cost.discount ? cost.discount.amount.toNumber() : '');
  setDiscountType((_cost$discount$type = (_cost$discount = cost.discount) === null || _cost$discount === void 0 ? void 0 : _cost$discount.type) !== null && _cost$discount$type !== void 0 ? _cost$discount$type : DiscountType.Percent);
  switch (cost.pricingType) {
    case 'fixed':
    case 'variable':
      setPrice(cost.price.toNumber());
      break;
    case 'range':
      setPrice({
        minPrice: cost.minPrice ? cost.minPrice.toNumber() : '',
        maxPrice: cost.maxPrice.toNumber()
      });
      break;
  }
}
export function ServicePriceEditor(props) {
  var _price$minPrice, _price$minPrice2;
  const getProps = useStateInRef(props);
  const {
    cost,
    editorAnchor,
    isOpen,
    onInputFocus,
    onInputBlur
  } = props;
  const [price, setPrice] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountType, setDiscountType] = useState(DiscountType.Percent);
  const unitName = cost.pricingType === 'variable' && (cost.unitName || 'hr');
  useEffect(() => resetForm(cost, setPrice, setDiscount, setDiscountType), [cost]);
  const discountedPrice = useMemo(() => {
    const calcPrice = price => utils.applyDiscount(price, discount ? {
      type: discountType,
      amount: new Decimal(discount)
    } : null).toNumber();
    if (typeof price === 'number') {
      return calcPrice(price);
    } else if (typeof price === 'object' && price.maxPrice) {
      return {
        minPrice: typeof price.minPrice !== 'number' ? undefined : calcPrice(price.minPrice),
        maxPrice: calcPrice(price.maxPrice)
      };
    }
    return '';
  }, [discount, discountType, price]);
  const priceError = useMemo(() => {
    if (!price && price !== 0) {
      return 'Provide a price of 0 or more';
    } else if (typeof price !== 'number' && !price.maxPrice) {
      return 'Insert price';
    }
    return '';
  }, [price]);
  const minPriceError = useMemo(() => {
    if (price && typeof price !== 'number' && price.minPrice && price.maxPrice && price.minPrice >= price.maxPrice) {
      return "Min can't exceed or equal max";
    }
    return '';
  }, [price]);
  const discountError = useMemo(() => {
    if (discount !== '' && price !== '') {
      if (discountType === 'value') {
        if (typeof price === 'number' && price < discount) {
          return "Discount value can't exceed the price";
        } else if (typeof price === 'object') {
          if (price.minPrice && price.minPrice < discount) {
            return "Discount value can't exceed min price";
          } else if (typeof price.maxPrice === 'number' && price.maxPrice < discount) {
            return "Discount value can't exceed max price";
          }
        }
      } else if (discountType === 'percent' && discount > 100) {
        return 'Provide a value up to 100%';
      }
    }
    return '';
  }, [discount, discountType, price]);
  const hasError = useMemo(() => !!priceError || !!minPriceError || !!discountError, [discountError, minPriceError, priceError]);
  function onPriceChange(e) {
    const newValue = e.currentTarget.value;
    const asNumber = +newValue;
    if (!newValue) {
      setPrice('');
    } else if (asNumber >= 0) {
      setPrice(asNumber);
    }
  }
  function onMinPriceChange(e) {
    const newValue = e.currentTarget.value;
    const asNumber = +newValue;
    if (asNumber < 0) return;
    setPrice({
      minPrice: !newValue ? '' : asNumber,
      maxPrice: price && typeof price === 'object' && 'maxPrice' in price ? price.maxPrice : ''
    });
  }
  function onMaxPriceChange(e) {
    const newValue = e.currentTarget.value;
    const asNumber = +newValue;
    if (asNumber < 0) return;
    setPrice({
      minPrice: price && typeof price === 'object' && 'maxPrice' in price ? price.minPrice : '',
      maxPrice: !newValue ? '' : asNumber
    });
  }
  function onDiscountChange(e) {
    const newValue = e.currentTarget.value;
    const asNumber = +newValue;
    if (!newValue) {
      setDiscount('');
    } else if (asNumber >= 0) {
      setDiscount(asNumber);
    }
  }
  function onSave(e) {
    e.preventDefault();
    if (hasError) return;
    const {
      cost,
      onCostChange,
      setIsOpen
    } = getProps();
    const edit = cloneDeep(cost);
    edit.discount = discount ? {
      type: discountType,
      amount: new Decimal(discount)
    } : null;
    switch (edit.pricingType) {
      case 'fixed':
      case 'variable':
        edit.price = new Decimal(+price);
        break;
      case 'range':
        if (price && typeof price === 'object') {
          edit.minPrice = price.minPrice || price.minPrice === 0 ? new Decimal(+price.minPrice) : null;
          edit.maxPrice = new Decimal(+price.maxPrice);
        }
        break;
    }
    onCostChange(edit);
    setIsOpen(false);
  }
  const onDismiss = useCallback(() => {
    const {
      cost,
      setIsOpen
    } = getProps();
    setIsOpen(false);
    setTimeout(() => resetForm(cost, setPrice, setDiscount, setDiscountType), 100); // apr. time takes the popover to close
  }, []);
  return /*#__PURE__*/React.createElement(Popover, {
    classes: {
      paper: styles.popover
    },
    open: isOpen,
    anchorEl: editorAnchor,
    onClose: onDismiss
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: onSave,
    className: styles.popoverForm
  }, /*#__PURE__*/React.createElement(CloseButton, {
    className: styles.closeBtn,
    "data-testid": "price-editor-popover-close",
    onClick: onDismiss
  }), /*#__PURE__*/React.createElement(Grid, null, typeof price === 'number' || price === '' ? /*#__PURE__*/React.createElement(GridItem, {
    span: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    isMoney: true,
    autoFocus: true,
    inputProps: {
      min: 0
    },
    testid: "price-editor-value",
    label: "Price",
    prefix: "$",
    suffix: unitName && "/".concat(unitName),
    value: price,
    onChange: onPriceChange,
    error: !!priceError,
    helperText: priceError,
    onFocus: onInputFocus,
    onBlur: onInputBlur
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GridItem, {
    span: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    isMoney: true,
    autoFocus: true,
    inputProps: {
      min: 0
    },
    testid: "price-editor-min-value",
    label: "Min (optional)",
    prefix: "$",
    value: (_price$minPrice = price.minPrice) !== null && _price$minPrice !== void 0 ? _price$minPrice : '',
    onChange: onMinPriceChange,
    error: !!minPriceError,
    helperText: minPriceError,
    onFocus: onInputFocus,
    onBlur: onInputBlur
  })), /*#__PURE__*/React.createElement(GridItem, {
    span: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    isMoney: true,
    autoFocus: true,
    inputProps: {
      min: (_price$minPrice2 = price.minPrice) !== null && _price$minPrice2 !== void 0 ? _price$minPrice2 : 0
    },
    testid: "price-editor-max-value",
    label: "Max",
    prefix: "$",
    value: price.maxPrice,
    onChange: onMaxPriceChange,
    error: !!priceError,
    helperText: priceError,
    onFocus: onInputFocus,
    onBlur: onInputBlur
  }))), /*#__PURE__*/React.createElement(GridItem, {
    span: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    isPercentage: discountType === 'percent',
    isMoney: discountType === 'value',
    inputProps: {
      min: 0
    },
    testid: "price-editor-discount",
    label: discountType === 'percent' ? 'Discount' : 'Unit discount',
    value: discount,
    onChange: onDiscountChange,
    error: !!discountError,
    helperText: discountError,
    suffix: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(TextField, {
      select: true,
      value: discountType,
      onChange: e => setDiscountType(e.target.value),
      preventOutline: true
    }, /*#__PURE__*/React.createElement(MenuItem, {
      value: "value"
    }, "$"), /*#__PURE__*/React.createElement(MenuItem, {
      value: "percent"
    }, "%"))),
    onFocus: onInputFocus,
    onBlur: onInputBlur
  }))), !!discount && /*#__PURE__*/React.createElement("div", {
    "data-testid": "price-editor-final-price"
  }, /*#__PURE__*/React.createElement(Typography, {
    as: "span",
    weight: "normal"
  }, "Price after discount:", ' '), !hasError && /*#__PURE__*/React.createElement(Typography, {
    as: "span"
  }, price === '' || typeof price === 'number' || discountedPrice === '' || typeof discountedPrice === 'number' ? /*#__PURE__*/React.createElement(Typography, {
    as: "span"
  }, "$", typeof discountedPrice === 'number' && utils.formatNumberWithCommas(discountedPrice), unitName && "/".concat(unitName)) : /*#__PURE__*/React.createElement(React.Fragment, null, !!discountedPrice.minPrice && !!price.minPrice && /*#__PURE__*/React.createElement(Typography, {
    as: "span"
  }, utils.formatDollarAmount(discountedPrice.minPrice), ' - '), !discountedPrice.minPrice && /*#__PURE__*/React.createElement(Typography, {
    as: "span"
  }, 'Up to '), /*#__PURE__*/React.createElement(Typography, {
    as: "span"
  }, "$", utils.formatNumberWithCommas(discountedPrice.maxPrice))))), /*#__PURE__*/React.createElement("div", {
    className: styles.popoverFooter
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    skin: "primaryOutline",
    onClick: onDismiss,
    "data-testid": "price-editor-cancel"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    disabled: hasError,
    "data-testid": "price-editor-save"
  }, "Save"))));
}